import { Link } from "react-router-dom";
import { FaTrophy } from "react-icons/fa";
import { formatHtml } from "../utils/formatHtml";
import PillarGraphic from "./PillarGraphic";
import { researchPillars } from "../data/researchPillars";

const pillarById = Object.fromEntries(
  researchPillars.map((pillar) => [pillar.id, pillar]),
);

const DESCRIPTION_CLASSES =
  "mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300 [&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2";

const MEDIA_WRAP =
  "flex aspect-[16/10] max-h-44 w-full items-center justify-center border-b border-gray-200/70 bg-white p-3 dark:border-gray-800/70 dark:bg-darkBg";

const PILLAR_MEDIA_WRAP =
  "flex aspect-[16/10] max-h-36 w-full items-center justify-center border-b border-gray-200/70 bg-white p-3 dark:border-gray-800/70 dark:bg-darkBg sm:aspect-[4/3] sm:max-h-72 sm:p-4";

const PILLAR_DESCRIPTION_CLASSES =
  "mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300 sm:line-clamp-6 [&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2";

/** Non-interactive: cards may be wrapped in a <button>. */
const PillarBadges = ({ pillarIds = [] }) => {
  const pillars = pillarIds.map((id) => pillarById[id]).filter(Boolean);
  if (!pillars.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {pillars.map((pillar) => (
        <span
          key={pillar.id}
          title={pillar.title}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-white/70 py-0.5 pl-0.5 pr-2 text-sm font-medium text-green dark:text-green-dark dark:border-gray-700 dark:bg-darkBg/70"
        >
          <PillarGraphic
            pillarId={pillar.id}
            className="h-5 w-6 shrink-0 overflow-hidden rounded-full"
          />
          <span className="truncate">{pillar.bigGoal || pillar.id}</span>
        </span>
      ))}
    </div>
  );
};

/**
 * Shared card for research pillars and projects.
 * Pass `onClick` for pillar/modal open; pass `id` for project detail link.
 * Pass `variant="pillar"` for taller media + more description room.
 * Pass `pillarIds` to show one or more agenda theme badges on project cards.
 */
const ProjectCard = ({
  img,
  media,
  title,
  year,
  description,
  tags = [],
  pillarIds = [],
  id,
  backTo = "/home?view=projects",
  backLabel = "Research",
  detailBase = "projects",
  showViewMore = true,
  onClick,
  ctaLabel = "View more →",
  className = "",
  variant = "default",
}) => {
  const detailPath = id ? `/${detailBase}/${id}` : null;
  const hasHtml = description && /<[^>]+>/.test(description);
  const isInteractive = typeof onClick === "function";
  const isPillar = variant === "pillar";
  const mediaWrapClass = isPillar ? PILLAR_MEDIA_WRAP : MEDIA_WRAP;
  const descriptionClass = isPillar
    ? PILLAR_DESCRIPTION_CLASSES
    : DESCRIPTION_CLASSES;

  const body = (
    <>
      {(media || img) && (
        <div className={mediaWrapClass}>
          {media || (
            <img
              src={img}
              alt={`Thumbnail for ${title}`}
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col p-4 lg:p-5">
        <h3 className="text-base font-semibold leading-tight text-lightText dark:text-darkText">
          {title}
        </h3>

        {year && (
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
            {year}
          </p>
        )}

        {!isPillar && <PillarBadges pillarIds={pillarIds} />}

        {description &&
          (hasHtml ? (
            <div
              className={descriptionClass}
              dangerouslySetInnerHTML={{ __html: formatHtml(description) }}
            />
          ) : (
            <p className={descriptionClass}>{description}</p>
          ))}

        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {tags.map((tag, idx) => {
              const isObject = tag && typeof tag === "object";
              const label = isObject ? tag.label : tag;
              const awarded = isObject && tag.awarded;
              if (!label) return null;
              return (
                <span
                  key={`${label}-${idx}`}
                  className="inline-flex items-center gap-1 rounded-full bg-green/10 px-2 py-0.5 text-sm font-medium text-green dark:text-green-dark dark:bg-green/15"
                >
                  {awarded ? (
                    <FaTrophy
                      size={10}
                      className="shrink-0 text-amber-700 dark:text-amber-300"
                      aria-hidden="true"
                    />
                  ) : null}
                  {label}
                </span>
              );
            })}
          </div>
        )}

        {showViewMore && (
          <div className="mt-4 pt-1">
            {isInteractive || !detailPath ? (
              <span className="text-sm font-medium text-green dark:text-green-dark">{ctaLabel}</span>
            ) : (
              <Link
                to={detailPath}
                state={{ backTo, backLabel }}
                className="text-sm font-medium text-green dark:text-green-dark underline underline-offset-2"
                onClick={(e) => e.stopPropagation()}
              >
                {ctaLabel}
              </Link>
            )}
          </div>
        )}
      </div>
    </>
  );

  const shellClass = `flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white/40 text-left dark:border-gray-800 dark:bg-gray-900/20 transition-shadow hover:shadow-sm ${className}`;

  if (isInteractive) {
    return (
      <button type="button" onClick={onClick} className={`${shellClass} w-full`}>
        {body}
      </button>
    );
  }

  return <article className={shellClass}>{body}</article>;
};

export default ProjectCard;
